import {
  Component,
  HostBinding,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { Adjustment } from 'app/event/models/adjustment';
import { OnyxEvent } from '../../models/event';
import { ConfirmationModalComponent } from 'app/shared/confirmation-modal/confirmation-modal.component';
import { DecimalPipe, PercentPipe, UpperCasePipe } from '@angular/common';
import { AdjustmentStore } from '../../service/adjustment.store';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { ReservationStore } from 'app/event/service/reservation-store';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { Payment } from '../../models/payment';
import { PaymentStore } from '../../service/payment.store';
import { RsvpStore } from '../../service/rsvp.store';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { NumberUtils } from '../../../shared/utils/number.util';

@Component({
  selector: 'pioneer-payment-adjustment-equation',
  templateUrl: './payment-adjustment-equation.component.html',
  styleUrls: [ './payment-adjustment-equation.component.css' ]
})
export class PaymentAdjustmentEquationComponent implements OnInit {

  @HostBinding('class') defaultClasses = 'col form-container due-amount';
  @HostBinding('class.has-adjust') hasAdjustmentCssClass = false;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  @Input() isEditMode = false;

  @Input()
  @HostBinding('class.status-rejected')
  strikeThroughPaymentLines = false;

  @Input()
  strikeThroughPrepayment = false;

  confirmationModalConfig = new ConfirmationModalConfig();

  private adjustmentToRemove: Adjustment = null;
  public onyxEvent: OnyxEvent;
  public prepayment: Payment;

  constructor(
    private decimalPipe: DecimalPipe,
    private adjustmentStore: AdjustmentStore,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private reservationStore: ReservationStore,
    private rsvpStore: RsvpStore,
    private paymentStore: PaymentStore,
    private uppercasePipe: UpperCasePipe,
    private authenticationStore: AuthenticationStore
  ) {}

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit() {

    this.areThereAnyAdjustments().subscribe( hasAdjustments => {
      if (hasAdjustments) {
        this.hasAdjustmentCssClass = true;
      }
    });

    this.getPrepayment().subscribe(prepayment => {
      this.prepayment = prepayment;
      if (!!prepayment) {
        this.hasAdjustmentCssClass = true;
      }
    });

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'PaymentEquationComponent'
        })
      );
    }
  }

  get adjustments(): Observable<List<Adjustment>> {
    return this.adjustmentStore.adjustments;
  }

  get calculatedPayment(): Observable<number> {
    return this.adjustmentStore.getCalculatedPayment(ReservationStatusCode.CHECKED_OUT, this.onyxEvent.eventProperty.subscriber)
      .map((adjustments: number) => {
        return (adjustments ? adjustments : 0) - (this.prepayment ? this.prepayment.commissionAmount : 0);
    });
  }

  get totalCommissions(): Observable<number> {
    if (this.onyxEvent.eventProperty.subscriber) {
      return this.reservationStore.getTotalCommissions(ReservationStatusCode.CHECKED_OUT);
    } else {
      return this.rsvpStore.totalCommission;
    }
  }

  get totalRevenue(): Observable<number> {
    if (this.onyxEvent.eventProperty.subscriber) {
      return this.reservationStore.getTotalRevenue(ReservationStatusCode.CHECKED_OUT);
    } else {
      return this.rsvpStore.totalRevenue;
    }
  }

  areThereAnyAdjustments(): Observable<boolean> {
    return this.adjustments.map(list => list.size > 0);
  }

  getAdjustmentCssClass(adjustment: Adjustment): string {
    if (adjustment.adjustmentAmount >= 0) {
      return 'adjustment up';
    } else {
      return 'adjustment down';
    }
  }

  getAdjustmentAmountCssClass(adjustment: Adjustment): string {
    if (adjustment.adjustmentAmount >= 0) {
      return 'currency text-success';
    } else {
      return 'currency text-warning';
    }
  }

  confirmBeforeDeletingAdjustment(adjustment: Adjustment) {
    this.adjustmentToRemove = adjustment;

    // set up the title of the confirmation modal
    const label = 'EVENT.COMMISSION.DELETE_ADJUSTMENT_CONFIRMATION_TITLE';
    const params = {
      adjustmentAmount: this.decimalPipe.transform(adjustment.adjustmentAmount, '1.2-2'),
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    this.confirmationModalConfig.title = this.translateService.instant(label, params);

    this.confirmationModal.open();
  }

  deleteAdjustment(adjustmentId: number, eventId: number): void {
    this.adjustmentStore
      .deleteAdjustment(adjustmentId, eventId)
      .subscribe();
  }

  confirmBeforeDeletingAdjustmentHandler(value: any) {
    if (value) {
      const adjustmentId = this.adjustmentToRemove.eventAdjustmentId;
      const eventId = this.onyxEvent.eventId;
      this.deleteAdjustment(adjustmentId, eventId);
    }
    this.adjustmentToRemove = null;
  }

  private getPrepayment(): Observable<Payment> {
    return this.paymentStore.getPrepayment()
      .map((prepayment) => {
        return prepayment && prepayment.status ? prepayment : null;
      }).share();
  }

  /**
   *  If it is an agency user and the event property is subscriber,
   *  the addAdjustment button must be disabled
   *  @returns {boolean}
   */
  disableDeleteAdjustmentButton(): boolean {
    let hide: boolean;
    if (this.authenticationStore.isAgencyUser() && this.onyxEvent.eventProperty.subscriber) {
      hide = true;
    } else {
      hide = false;
    }
    return hide;
  }

  getAmount(amount) {
    return amount ? amount : 0;
  }

  get commissionPercentage() {
    return this.onyxEvent.commissionRoomPercentage ? this.onyxEvent.commissionRoomPercentage / 100 : 0;
  }

  isReducedCommission(totalRevenue, totalCommission) {
    return NumberUtils.round2Fixed(totalCommission) < NumberUtils.round2Fixed(totalRevenue * this.commissionPercentage);
  }

  getReducedCommissionTooltip() {
    return this.onyxEvent.eventProperty.subscriber ? 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RESERVATIONS'
      : 'EVENT.COMMISSION.TOOLTIP_REDUCED_COMMISSION_RSVPS';
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/payment/payment-adjustment-calculation/payment-adjustment-equation.component.ts